import { ITEM_DATABASE } from '../../../shared/ItemData';
import { getAbilityData } from '../../../shared/content/AbilityDataAdapter';
import { abilityStatLine } from '../../../shared/content/abilityText';
import { periodicAbilityIdForEffect } from '../../../shared/combat/periodicAbilityStatus';
import {
  ABILITY_EFFECT_DATABASE,
  type AbilityEffectDataDef,
} from '../../../shared/content/generated/ability-effects.gen';
import { STATUS_AURA_CONFIG } from '../../../config/abilities/statusAura.config';
import { resolvePipStyle } from '../../../config/ui/targetStatusPips.config';
import type { TooltipData } from '../components/Tooltip';

export type StatusDisposition = 'helpful' | 'harmful' | 'neutral';

export interface StatusPresentation {
  effectId: string;
  name: string;
  icon: string;
  color: number;
  disposition: StatusDisposition;
  description: string;
  details: string[];
  statBonuses: Array<{ label: string; value: number }>;
  sourceName?: string;
}

export interface ResolveStatusPresentationOptions {
  data?: Record<string, unknown> | null;
  name?: string;
  icon?: string;
  color?: number;
  sourceName?: string;
}

const STATUS_COPY: Record<string, {
  name: string;
  icon: string;
  disposition: StatusDisposition;
  description: string;
}> = {
  burn: {
    name: 'Burning',
    icon: '🔥',
    disposition: 'harmful',
    description: 'Taking periodic fire damage.',
  },
  poison: {
    name: 'Poisoned',
    icon: '☠',
    disposition: 'harmful',
    description: 'Taking periodic poison damage. Can be removed by an antidote.',
  },
  slow: {
    name: 'Slowed',
    icon: '🐌',
    disposition: 'harmful',
    description: 'Movement speed is reduced.',
  },
  frozen: {
    name: 'Frozen',
    icon: '❄',
    disposition: 'harmful',
    description: 'Movement is prevented and frost reactions may trigger.',
  },
  stun: {
    name: 'Stunned',
    icon: '💫',
    disposition: 'harmful',
    description: 'Unable to act.',
  },
  regen: {
    name: 'Regeneration',
    icon: '✚',
    disposition: 'helpful',
    description: 'Health is restored periodically.',
  },
  agony: {
    name: 'Agony',
    icon: '🩸',
    disposition: 'harmful',
    description: 'Suffering periodic shadow damage.',
  },
  corruption: {
    name: 'Corruption',
    icon: '🌀',
    disposition: 'harmful',
    description: 'Suffering periodic shadow damage.',
  },
  immolate: {
    name: 'Immolate',
    icon: '🔥',
    disposition: 'harmful',
    description: 'Burning from periodic fire damage.',
  },
  unstable_affliction: {
    name: 'Unstable Affliction',
    icon: '☣',
    disposition: 'harmful',
    description: 'Suffering volatile periodic shadow damage.',
  },
  curse_of_elements: {
    name: 'Curse of the Elements',
    icon: '🌩',
    disposition: 'harmful',
    description: 'Elemental defenses are compromised.',
  },
};

const EFFECT_ICONS: Record<string, string> = {
  heal_over_time_100: '✚',
  buff_speed_60: '⚡',
  buff_fortitude_100: '♥',
  buff_titans_allstats: '✦',
  buff_attack_10: '⚔',
  buff_defense_10: '◆',
  buff_experience_2x: '★',
};

const STAT_LABELS: Record<string, string> = {
  hp: 'Maximum Health',
  mp: 'Maximum Mana',
  attack: 'Attack',
  magicAttack: 'Magic Attack',
  defense: 'Defense',
  strength: 'Strength',
  dexterity: 'Dexterity',
  intelligence: 'Intelligence',
  luck: 'Luck',
  speed: 'Movement Speed',
  criticalDamage: 'Critical Damage',
};

// These converted abilities replicate their saved ability ID as the status ID.
// Keep raw debuffs and consumable definitions on their existing presentation path.
const CONVERTED_STATUS_ABILITIES = new Set(['avatar', 'avenging_wrath', 'windfury_totem']);

export function resolveStatusPresentation(
  effectId: string,
  options: ResolveStatusPresentationOptions = {},
): StatusPresentation {
  const definitionId = typeof options.data?.definitionId === 'string'
    ? options.data.definitionId
    : effectId;
  const canonical = ABILITY_EFFECT_DATABASE[definitionId]
    ?? ABILITY_EFFECT_DATABASE[effectId];
  const authored = STATUS_COPY[effectId];
  const abilityId = typeof options.data?.abilityId === 'string' ? options.data.abilityId : effectId;
  const periodicAbilityId = periodicAbilityIdForEffect(effectId);
  const marker = options.data?.abilityStatus as { kinds?: unknown } | undefined;
  const periodic = !canonical && periodicAbilityId !== undefined && options.data?.abilityId === periodicAbilityId
    && Array.isArray(marker?.kinds) && marker.kinds.includes('periodic_damage');
  const exposure = !canonical && effectId === 'curse_of_elements' && options.data?.abilityId === effectId
    && Array.isArray(marker?.kinds) && marker.kinds.includes('incoming_damage');
  const ability = periodic || exposure ? getAbilityData(abilityId)
    : !canonical && !authored && abilityId === effectId && CONVERTED_STATUS_ABILITIES.has(effectId)
      ? getAbilityData(abilityId) : undefined;
  const disposition = ability ? (periodic || exposure ? 'harmful' : 'helpful') : resolveDisposition(canonical, authored?.disposition);
  const pipStyle = resolvePipStyle(effectId);
  const auraColor = periodic || exposure ? ability?.vfx?.auraColor : undefined;
  const canonicalColor = auraColor && /^#[0-9a-f]{6}$/i.test(auraColor)
    ? Number.parseInt(auraColor.slice(1), 16) : STATUS_AURA_CONFIG[effectId]?.color;
  const itemId = typeof options.data?.itemId === 'string' ? options.data.itemId : undefined;
  const sourceName = itemId ? ITEM_DATABASE[itemId]?.name ?? humanizeId(itemId) : undefined;
  const statBonuses = Object.entries(canonical?.statBonuses ?? {})
    .filter((entry): entry is [string, number] =>
      typeof entry[1] === 'number' && Number.isFinite(entry[1])
    )
    .map(([key, value]) => ({
      label: STAT_LABELS[key] ?? humanizeId(key),
      value,
    }));

  return {
    effectId,
    name: ability?.name || options.name || canonical?.name || authored?.name || humanizeId(effectId),
    icon: ability ? `/icons/abilities/${ability.id}.svg` : options.icon || EFFECT_ICONS[definitionId] || authored?.icon || pipStyle.glyph,
    color: options.color ?? canonicalColor ?? dispositionColor(disposition),
    disposition,
    description: ability?.description || authored?.description || describeCanonicalEffect(canonical, disposition),
    details: ability ? abilityStatLine(ability.id, {})
      .filter(stat => periodic ? stat.key === 'periodic' : exposure ? stat.key === 'exposure'
        : stat.key === 'output' || stat.key === 'reduction' || stat.key === 'radius')
      .map(stat => `${stat.label} ${stat.value}`) : describeEffectDetails(canonical),
    statBonuses,
    sourceName: sourceName ?? options.sourceName,
  };
}

export function createStatusTooltip(
  presentation: StatusPresentation,
  remainingMs: number,
  stacks: number = 1,
): TooltipData {
  const dispositionLabel = presentation.disposition === 'helpful'
    ? 'Helpful effect'
    : presentation.disposition === 'harmful'
      ? 'Harmful effect'
      : 'Status effect';
  const extras = [
    remainingMs > 0 ? `${formatExactRemainingDuration(remainingMs)} remaining` : 'No fixed duration',
    stacks > 1 ? `${stacks} stacks` : '',
    presentation.sourceName ? `Source: ${presentation.sourceName}` : '',
  ].filter(Boolean);

  return {
    title: presentation.name,
    accentColor: colorNumberToCss(presentation.color),
    description: `${dispositionLabel}. ${presentation.description}`,
    stats: presentation.statBonuses,
    effects: presentation.details,
    effectLabel: 'Effect',
    extraInfo: extras.join(' • '),
  };
}

export function formatExactRemainingDuration(remainingMs: number): string {
  if (remainingMs <= 0) return 'Expired';
  const seconds = Math.ceil(remainingMs / 1000);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

function resolveDisposition(
  canonical: AbilityEffectDataDef | undefined,
  authored?: StatusDisposition,
): StatusDisposition {
  if (authored) return authored;
  if (!canonical) return 'neutral';
  if (canonical.effectType === 'buff' || canonical.effectType === 'hot' || canonical.effectType === 'shield') {
    return 'helpful';
  }
  if (
    canonical.effectType === 'debuff'
    || canonical.effectType === 'dot'
    || canonical.effectType === 'stun'
    || canonical.effectType === 'slow'
    || canonical.effectType === 'root'
    || canonical.effectType === 'silence'
  ) return 'harmful';
  return 'neutral';
}

function describeCanonicalEffect(
  effect: AbilityEffectDataDef | undefined,
  disposition: StatusDisposition,
): string {
  if (!effect) {
    return disposition === 'helpful'
      ? 'A beneficial effect is active.'
      : disposition === 'harmful'
        ? 'A harmful effect is active.'
        : 'An active status effect.';
  }
  if (effect.effectType === 'hot') {
    return `Restores ${effect.baseValue} ${capitalize(effect.restoreResource ?? 'health')} over time.`;
  }
  if (effect.effectType === 'buff') return 'Temporarily improves character attributes.';
  return humanizeId(effect.effectType);
}

function describeEffectDetails(effect: AbilityEffectDataDef | undefined): string[] {
  if (!effect) return [];
  if (effect.effectType === 'hot') {
    return [
      `Restores ${effect.baseValue} ${capitalize(effect.restoreResource ?? 'health')} over ${formatAuthoredDuration(effect.durationSec)}.`,
    ];
  }
  if (effect.effectType === 'buff' && effect.statBonuses) {
    return Object.entries(effect.statBonuses).map(([key, value]) =>
      `Increases ${STAT_LABELS[key] ?? humanizeId(key)} by ${value}.`
    );
  }
  if (effect.effectType === 'buff' && effect.id === 'buff_experience_2x') {
    return ['Doubles experience earned.'];
  }
  return [];
}

function formatAuthoredDuration(seconds: number): string {
  if (seconds >= 3600) return `${seconds / 3600} hours`;
  if (seconds >= 60 && seconds % 60 === 0) return `${seconds / 60} minutes`;
  return `${seconds} seconds`;
}

function dispositionColor(disposition: StatusDisposition): number {
  if (disposition === 'helpful') return 0x4ade80;
  if (disposition === 'harmful') return 0xf87171;
  return 0xcbd5e1;
}

function colorNumberToCss(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

function humanizeId(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
